import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { isWeb } from 'shared/constants/platform';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import {
  showZendeskSection,
  ZendeskSections,
} from 'shared/utils/zendesk/zendeskUtils';

const LineDivider = () => {
  return (
    <View style={styles.lineContainer}>
      <HairLineDivider />
    </View>
  );
};

const CardBasicsScreen = () => {
  const router = useRouter();
  const { bottom } = useSafeAreaInsets();
  const { accountId } = useLocalSearchParams<{ accountId: string }>();
  const { t: translatedText } = useTranslation('believe', {
    keyPrefix: 'learningCenter',
  });

  const handleShowBelieveSection = () => {
    showZendeskSection(ZendeskSections.VARO_BELIEVE);
  };

  return (
    <ScrollView style={[styles.screen, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          title: translatedText('title'),
          headerBackVisible: true,
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ListItem
        leadingAccessory={{
          id: 'LEARNING_CENTER_HOW_WORKS',
          name: VXLComponentId.ICON,
          props: {
            iconKey: 'CARD',
          },
        }}
        onPress={() => {
          router.push({
            pathname: '/believe/how-it-works/how-works',
            params: { accountId: accountId },
          });
        }}
        primaryCopy={translatedText('basicScreenOptions.howWorks')}
        analyticsId="how-works-pressed"
        customAttributes={{ faq_name: 'how_charge_card_works' }}
      />
      <LineDivider />
      <ListItem
        leadingAccessory={{
          id: 'LEARNING_CENTER_HOW_BUILD_CREDIT',
          name: VXLComponentId.ICON,
          props: {
            iconKey: 'VAULT',
          },
        }}
        onPress={() => {
          router.push({
            pathname: '/believe/how-it-works/how-build-credit',
            params: { accountId: accountId },
          });
        }}
        primaryCopy={translatedText('basicScreenOptions.howBuildCredit')}
        analyticsId="how-build-creditPressed"
        customAttributes={{ faq_name: 'build_credit' }}
      />
      <LineDivider />
      <ListItem
        leadingAccessory={{
          id: 'LEARNING_CENTER_CARD_BENEFITS',
          name: VXLComponentId.ICON,
          props: {
            iconKey: 'DEPOSIT_CASH',
          },
        }}
        onPress={() => {
          router.push('/believe/how-it-works/card-benefits');
        }}
        primaryCopy={translatedText('basicScreenOptions.cardBenefits')}
        analyticsId="card-benefitsPressed"
        customAttributes={{ faq_name: 'card_benefits' }}
      />
      <LineDivider />
      <ListItem
        leadingAccessory={{
          id: 'LEARNING_CENTER_SPENDING_LIMIT',
          name: VXLComponentId.ICON,
          props: {
            iconKey: 'LOAN',
          },
        }}
        onPress={() => {
          router.push('/believe/how-it-works/spending-limit');
        }}
        primaryCopy={translatedText('basicScreenOptions.spendingLimit')}
        analyticsId="spending-limitPressed"
        customAttributes={{ faq_name: 'spending_limit' }}
      />
      <LineDivider />
      <ListItem
        leadingAccessory={{
          id: 'LEARNING_CENTER_SUPPORT_TOPICS',
          name: VXLComponentId.ICON,
          props: {
            iconKey: 'QUESTION_MARK',
          },
        }}
        onPress={() => {
          handleShowBelieveSection();
        }}
        primaryCopy={translatedText('basicScreenOptions.supportTopics')}
        analyticsId="supportTopicsPressed"
        customAttributes={{ faq_name: 'support_topics' }}
      />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  lineContainer: {
    paddingHorizontal: theme.spacing.m,
  },
  screen: {
    backgroundColor: theme.color.background.primary,
    flex: isWeb ? undefined : 1,
    paddingVertical: theme.spacing.m,
  },
});

export default CardBasicsScreen;
